import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import {
  mdiCloseOutline,
  mdiContentSaveOutline,
  mdiDeleteOutline,
  mdiListBoxOutline,
  mdiPencilOutline,
  mdiPlus,
} from "@mdi/js";
import { Box, Button, Grid, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import cloneDeep from "lodash.clonedeep";
import Icon from "@mdi/react";

import SecretSantaApi, { SECRET_SANTA_ACTIONS } from "api/secret-santa";
import useNotifier from "hooks/useNotifier";
import PageLoader from "components/PageLoader";
import CircularLoader from "components/CircularLoader";
import Wishlist from "../Wishlist";

const validationSchema = yup.object().shape({
  wishlist: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.string().required("Required").max(300, "Must be less than 300 characters."),
      })
    )
    .required(),
  duplicate: yup.string().test("unique", "Wishlist items can not be duplicated.", (_value, context) => {
    const values = context.parent.wishlist?.map((item: FormValues["wishlist"][0]) => item.value);
    return values?.length === new Set(values)?.size;
  }),
});

const LIST_MODE = {
  EDIT: "EDIT",
  STATIC: "STATIC",
} as const;

type ListMode = keyof typeof LIST_MODE;

type FormValues = {
  wishlist: Array<{ value: string }>;
  duplicate?: string;
};

export interface MyWishlistProps {
  eventId: number;
  groupId: number;
  isAppFocused: boolean;
  readonly?: boolean;
  userAuthId?: number;
}

export default function MyWishlist(props: MyWishlistProps) {
  const { eventId, groupId, readonly, userAuthId } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [mode, setMode] = useState<ListMode>("STATIC");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [initialState, setInitialState] = useState<FormValues["wishlist"]>();
  const {
    control,
    formState: { errors },
    handleSubmit,
    getValues,
  } = useForm<FormValues>({
    defaultValues: { wishlist: [] },
    resolver: yupResolver(validationSchema),
  });
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "wishlist",
  });
  const { showNotification } = useNotifier();
  const secretSantaApi = useMemo(() => new SecretSantaApi(showNotification), [showNotification]);

  useEffect(() => {
    (async () => {
      if (userAuthId) {
        const { data } = await secretSantaApi.getMyWishListForSanta(eventId, userAuthId);
        if (data) {
          replace(data.map((item) => ({ value: item })));
        }
        setIsLoading(false);
      }
    })();
  }, [userAuthId]);

  useEffect(() => {
    if (mode === LIST_MODE.EDIT) {
      setInitialState(cloneDeep(getValues("wishlist")));
      if (!fields.length) {
        append({ value: "" });
      }
    }
  }, [mode]);

  function handleAddWishlist() {
    setMode(LIST_MODE.EDIT);
  }

  async function handleSaveWishlist(data: FormValues) {
    try {
      if (JSON.stringify(initialState) === JSON.stringify(getValues().wishlist) && initialState) {
        replace(initialState);
        setMode(LIST_MODE.STATIC);
        return;
      }

      if (userAuthId) {
        setIsSaving(true);
        const { error } = await secretSantaApi.updateWishList(
          eventId,
          groupId,
          data.wishlist.map((item) => item.value),
          SECRET_SANTA_ACTIONS.UPDATE_WISHLIST
        );

        if (!error) {
          replace(data.wishlist);
          setMode(LIST_MODE.STATIC);
        }
      }
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteWishlist() {
    setIsDeleting(true);
    const { error } = await secretSantaApi.updateWishList(eventId, groupId, [], SECRET_SANTA_ACTIONS.UPDATE_WISHLIST);

    if (!error) {
      replace([]);
      setMode(LIST_MODE.STATIC);
      setIsDeleting(false);
    }
  }

  function handleCancel() {
    setMode(LIST_MODE.STATIC);
    if (initialState) {
      replace(initialState);
    }
  }

  function handleEditWishlist() {
    setMode(LIST_MODE.EDIT);
  }

  function handleAddWishItem() {
    append({ value: "" });
  }

  function deleteWishItem(index: number) {
    remove(index);
  }

  function renderEmpty() {
    return (
      <Box
        position='absolute'
        top='50%'
        left='50%'
        alignItems='center'
        color='text.secondary'
        display='flex'
        flexDirection='column'
        sx={{ transform: "translate(-50%,-50%)" }}
        width='100%'
        p={2}
      >
        <Icon path={mdiListBoxOutline} size={2} />
        <Typography align='center'>Help your Secret Santa by sharing a wishlist</Typography>
        {!readonly && (
          <Button
            sx={{ mt: 3 }}
            startIcon={<Icon path={mdiPlus} size={1} />}
            variant='contained'
            onClick={handleAddWishlist}
          >
            Add wishlist
          </Button>
        )}
      </Box>
    );
  }

  function renderEditableList() {
    return (
      <form onSubmit={handleSubmit(handleSaveWishlist)}>
        <Box display='flex' flexDirection='column'>
          <Grid container spacing={2}>
            {fields.map((item, index) => (
              <Grid item key={item.id} xs={12}>
                <Controller
                  key={item.id}
                  control={control}
                  name={`wishlist.${index}.value`}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      InputProps={{
                        endAdornment:
                          fields.length > 1 ? (
                            <InputAdornment position='end'>
                              <IconButton
                                aria-label={`Delete wish ${index + 1}`}
                                onClick={() => deleteWishItem(index)}
                                edge='end'
                              >
                                <Icon path={mdiDeleteOutline} size={1} />
                              </IconButton>
                            </InputAdornment>
                          ) : undefined,
                      }}
                      error={Boolean(errors.wishlist?.[index])}
                      fullWidth
                      helperText={errors.wishlist?.[index]?.value?.message}
                      label={`Wish ${index + 1}`}
                    />
                  )}
                />
              </Grid>
            ))}
            {/* Used to render duplicate error */}
            <Grid item xs={12}>
              <Controller
                control={control}
                name='duplicate'
                render={() => (
                  <Box>
                    <Typography color='error' variant='caption'>
                      {errors.duplicate?.message}
                    </Typography>
                  </Box>
                )}
              />
            </Grid>
          </Grid>

          {fields.length > 0 && (
            <>
              <Box alignSelf='flex-end'>
                {fields.length < 5 && (
                  <IconButton
                    aria-label='Add wish'
                    onClick={handleAddWishItem}
                    sx={{
                      mt: 1,
                      border: "1px solid ",
                      borderRadius: 1,
                      color: "primary.main",
                    }}
                  >
                    <Icon path={mdiPlus} size={1} />
                  </IconButton>
                )}
              </Box>

              <Box display='flex' justifyContent='flex-end' gap={2} mt={2}>
                {initialState?.length !== 0 ? (
                  <CircularLoader loading={isDeleting}>
                    <Button
                      startIcon={<Icon path={mdiDeleteOutline} size={1} />}
                      disabled={isSaving || isDeleting}
                      variant='contained'
                      onClick={handleDeleteWishlist}
                    >
                      Delete
                    </Button>
                  </CircularLoader>
                ) : (
                  <Button
                    startIcon={<Icon path={mdiCloseOutline} size={1} />}
                    disabled={isSaving || isDeleting}
                    variant='contained'
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                )}
                <CircularLoader loading={isSaving}>
                  <Button
                    type='submit'
                    disabled={isSaving || isDeleting}
                    startIcon={<Icon path={mdiContentSaveOutline} size={1} />}
                    variant='contained'
                  >
                    Save
                  </Button>
                </CircularLoader>
              </Box>
            </>
          )}
        </Box>
      </form>
    );
  }

  if (isLoading) {
    return <PageLoader />;
  }

  if (!fields.length && mode === LIST_MODE.STATIC) {
    return renderEmpty();
  }

  return mode === LIST_MODE.STATIC ? (
    <Box>
      <Wishlist items={fields.map((item) => item.value)} />
      {!readonly && (
        <Box display='flex' justifyContent='flex-end'>
          <Button
            startIcon={<Icon path={mdiPencilOutline} size={1} />}
            sx={{ mt: 2 }}
            variant='contained'
            onClick={handleEditWishlist}
          >
            Edit wishlist
          </Button>
        </Box>
      )}
    </Box>
  ) : (
    renderEditableList()
  );
}
